import { captureException } from '@sentry/react';
import invariant from 'tiny-invariant';

import { config as envConfig } from 'src/config';
import { DesignConfigCategories } from 'src/modules/designStudio/constants/designConfig';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { Partner } from 'src/modules/designStudio/helpers/Partner';
import { fetchDataWrapper } from 'src/modules/designStudio/hooks/useFetchModelData';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import { Mode } from 'src/modules/designStudio/providers';
import { mapPartnerIdToEnum } from 'src/modules/designStudio/providers/ModeProvider';
import { client as apolloClient } from 'src/shared/graphql';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import { DesignStudioThemeEnum } from 'src/shared/graphql/generated';
import type { GenericProductSettings } from 'src/shared/graphql/requests';
import { queries } from 'src/shared/graphql/requests';
import { requestWithRetry } from 'src/shared/utils/request';

import type {
  FilterTracker,
  FilteredColorsData,
  FilteredTexturesData,
  MenuItems,
  Textures,
  Thumbnail,
} from './types';

const PRODUCTS_ENDPOINT = envConfig.PRODUCTS_URI;

const GUEST_MACHETE_FILE_LOCATION = 'guest/machete_2_10_2023.json';
const GUEST_MODEL_FILE_LOCATION = 'guest/model_2_10_2023.json';

const GUEST_MACHETE_FILE_URL = `${
  envConfig.GUEST_FILE_PATH || process.env.PUBLIC_PATH || '/'
}${GUEST_MACHETE_FILE_LOCATION}`;
const GUEST_MODEL_FILE_URL = `${
  envConfig.GUEST_FILE_PATH || process.env.PUBLIC_PATH || '/'
}${GUEST_MODEL_FILE_LOCATION}`;

const addThumbnailsToMenuItems = ({
  menuItems,
  textures,
}: {
  menuItems: MenuItemType[];
  textures: Textures;
}) => {
  // add thumbnail urls from products endpoint
  const hydratedMenuItems = menuItems.map(item => {
    const thumbnail: Thumbnail = item.hasThumbnail
      ? textures?.find(texture => texture.id === item.textureId)?.texture
          ?.thumbnail.url
      : undefined;

    return {
      ...item,
      thumbnail,
    };
  });

  return { menuItems: hydratedMenuItems };
};

const filterMenuItems = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const updatedFilterTracker = filterTracker;
  if (settings) {
    const filteredMenuItems = menuItems.filter(menuItem => {
      if (settings[menuItem.category]?.disabled_types) {
        updatedFilterTracker[menuItem.category as DesignConfigCategories] =
          true;
      }
      return !settings[menuItem.category]?.disabled_types?.includes(
        menuItem.type,
      );
    });

    return {
      menuItems: filteredMenuItems,
      filterTracker: updatedFilterTracker,
    };
  }

  return { menuItems, filterTracker };
};

const filterTextures = (
  menuItems: MenuItemType[],
  filterTracker: FilterTracker,
  settings?: GenericProductSettings,
) => {
  const filteredTextures: FilteredTexturesData = {};
  const updatedFilterTracker = filterTracker;

  const isMenuItemFilteredById = (
    menuItem: MenuItemType,
    disabledTextureIds: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureIds[
      `${menuItem.type.toUpperCase()}::${menuItem.textureId}`
    ];

  const isMenuItemFilteredByColorGroup = (
    menuItem: MenuItemType,
    disabledTextureColorGroups: { [key: string]: boolean | undefined },
  ) =>
    !!disabledTextureColorGroups[
      `${menuItem.type.toUpperCase()}::${
        menuItem.colorGroup?.toUpperCase() ?? ''
      }`
    ];

  if (settings) {
    Object.keys(settings).forEach(category => {
      const filteredCategoryTextures =
        filteredTextures[category as DesignConfigCategories] ?? {};
      const categorySettings = settings[category] ?? {};

      const disabledTextureIds: { [key: string]: boolean } = {};
      (categorySettings.disabled_textures ?? []).forEach(disabledTexture => {
        disabledTextureIds[disabledTexture.toUpperCase()] = true;
      });

      const disabledTextureColorGroups: { [key: string]: boolean } = {};
      (categorySettings.disabled_texture_color_groups ?? []).forEach(
        disabledColorGroup => {
          disabledTextureColorGroups[disabledColorGroup.toUpperCase()] = true;
        },
      );

      menuItems.forEach(menuItem => {
        const isDisabled =
          isMenuItemFilteredById(menuItem, disabledTextureIds) ||
          isMenuItemFilteredByColorGroup(menuItem, disabledTextureColorGroups);

        if (isDisabled) {
          const filteredTypeTextures =
            filteredCategoryTextures[menuItem.type] || {};
          filteredTypeTextures[menuItem.textureId] = true;
          filteredCategoryTextures[menuItem.type] = filteredTypeTextures;
        }
      });

      if (Object.keys(filteredCategoryTextures).length > 0) {
        filteredTextures[category as DesignConfigCategories] =
          filteredCategoryTextures;
      }
    });
  }

  return { filteredTextures, filterTracker: updatedFilterTracker };
};

const filterColors = (
  partner: Partner | undefined,
  filterTracker: FilterTracker,
  colors: DesignStudioColor[],
  windowColors: DesignStudioColor[],
  settings?: GenericProductSettings,
) => {
  const filteredColors: FilteredColorsData = {
    [DesignConfigCategories.ROOF]: colors,
    [DesignConfigCategories.WALL]: colors,
    [DesignConfigCategories.TRIM]: colors,
    [DesignConfigCategories.WINDOW]: windowColors,
  };
  const updatedFilterTracker = filterTracker;

  if (settings) {
    Object.keys(settings).forEach(category => {
      const colorDataSet: DesignStudioColor[] =
        category === DesignConfigCategories.WINDOW ? windowColors : colors;

      const filtered = colorDataSet.filter(color => {
        if (settings[category]?.disabled_colors) {
          updatedFilterTracker[category as DesignConfigCategories] = true;
        }
        return !settings[category]?.disabled_colors?.includes(color.color);
      });

      filteredColors[category as DesignConfigCategories] = filtered;
    });
  } else if (partner === Partner.JAMES_HARDIE) {
    filteredColors[DesignConfigCategories.TRIM] = filteredColors[
      DesignConfigCategories.TRIM
    ].filter(color => !!color.trim);
  }

  return { filteredColors, filterTracker: updatedFilterTracker };
};

/**
 * @description In order to create a machete building, machete requires 2 files:
 * - model_json_v3.json: Otherwise known as the geometry file
 * - machete.json: Otherwise known as the metadata file.
 *
 * This function handles fetching these files through a graphQL query.
 * If DS3D is opened in guest or preview mode then we don't use the query, and use a hardcoded url.
 *
 * These files are needed in order for DS3D to load the 3D model.
 *
 */
const fetchModelData = async ({
  mode,
  jobId,
  shareToken,
  modelId,
}: {
  mode: Mode;
  jobId?: number;
  shareToken?: string;
  modelId?: number;
}) => {
  let geometryUrl;
  let metadataUrl;
  let authToken;
  let error;
  if (mode === Mode.GUEST) {
    geometryUrl = GUEST_MODEL_FILE_URL;
    metadataUrl = GUEST_MACHETE_FILE_URL;
  } else if (mode === Mode.PREVIEW) {
    geometryUrl = GUEST_MODEL_FILE_URL;
    metadataUrl = GUEST_MACHETE_FILE_URL;
  } else {
    invariant(jobId, 'Missing jobId');
    if (mode === Mode.SHARE) {
      const { data, error: shareError } = await queries.jobSharedThreeDFiles(
        apolloClient,
        {
          jobId,
          shareToken: shareToken || '',
        },
      );

      const resp = data?.jobSharedThreeDFiles ?? {};
      geometryUrl = resp.geometryUrl;
      metadataUrl = resp.metadataUrl;
      authToken = resp.authToken;
      error = shareError;
    }
  }
  const { metadata = null, geometry = null } = await fetchDataWrapper({
    geometryUrl,
    metadataUrl,
    setMetadata: () => {},
    setGeometry: () => {},
    jobId,
    modelId,
    mode,
  });
  return { metadata, geometry, authToken, error };
};

const fetchTextures = async () => {
  const textures = (await requestWithRetry(PRODUCTS_ENDPOINT)
    .then(res => res.json())
    .catch(error => {
      captureException(error);

      return [];
    })) as Textures;

  return {
    textures,
  };
};

const fetchPartner = async (jobId?: number, fallbackPartner?: Partner) => {
  let partner = fallbackPartner ?? Partner.DEFAULT;

  const partnerId = jobId
    ? await sharedOptions.fetchPartnerIdForJob(jobId)
    : undefined;

  if (partnerId) {
    partner = mapPartnerIdToEnum(partnerId, partner);
  }

  return { partner };
};

// called by setMenuItems, after partner is fetched
/**
 * @description This function fetches the product data used to populate the old
 * walls, roof, trim, and window menu. Currently this data is only used for interactive renderings.
 * This function needs to be called after the partner has been fetched.
 */
const fetchMenuItems = async (partner: Partner | undefined) => {
  const themeName =
    partner === Partner.JAMES_HARDIE
      ? DesignStudioThemeEnum.JamesHardieWithMagnolia
      : DesignStudioThemeEnum.Default;

  return sharedOptions.fetchMenuItems({
    themeName,
  });
};

/**
 * @description This function filters and sorts menu items. These menu items include color and textures
 * shown in the interactive renderings UI.
 */
const setMenuItems = ({
  menuItems,
  textures,
  productSettings,
  partner,
  initialFilterTracker,
}: {
  menuItems: MenuItems;
  textures: Textures;
  productSettings: GenericProductSettings | undefined;
  partner: Partner | undefined;
  initialFilterTracker: FilterTracker;
}): {
  menuItems: MenuItemType[];
  filteredColors: FilteredColorsData;
  filteredTextures: FilteredTexturesData;
  filterTracker: FilterTracker;
} => {
  const { menuItems: menuItemsWithThumbnails } = addThumbnailsToMenuItems({
    menuItems: menuItems?.textures,
    textures,
  });

  const {
    menuItems: filteredMenuItems,
    filterTracker: filterTrackerWithMenuItems,
  } = filterMenuItems(
    menuItemsWithThumbnails,
    initialFilterTracker,
    productSettings,
  );

  const { filteredColors, filterTracker: filterTrackerWithColors } =
    filterColors(
      partner,
      { ...filterTrackerWithMenuItems },
      menuItems.colors,
      menuItems.windowColors,
      productSettings,
    );

  const { filteredTextures, filterTracker } = filterTextures(
    menuItems.textures,
    filterTrackerWithColors,
    productSettings,
  );

  return {
    menuItems: filteredMenuItems,
    filteredColors,
    filteredTextures,
    filterTracker,
  };
};

/**
 * @description This function loads the job data associated with this 3d model.
 * @param jobId
 */
const fetchProductSettingOrgId = async ({ jobId }: { jobId?: number }) => {
  invariant(jobId, 'Missing jobId');
  const jobResponse = await queries.getJob(apolloClient, { id: jobId });
  return jobResponse.data.jobs.results[0];
};

const updateProductSettingsOrgId = (data: {
  orgId?: number;
  captureRequest?: {};
}) => {
  if (!data) {
    return {};
  }

  const captureRequest = data.captureRequest as {
    requestingUser: { orgs: { results: Array<{ id: number }> } };
  };

  const { orgId } = data;

  let productSettingsOrgId = orgId ?? undefined;

  if (captureRequest && captureRequest.requestingUser) {
    const requestingOrg = captureRequest.requestingUser.orgs.results[0];
    if (requestingOrg) {
      productSettingsOrgId = requestingOrg.id;
    }
  }

  return { productSettingsOrgId };
};

const fetchProductSettings = async ({
  productSettingsOrgId,
}: {
  productSettingsOrgId?: number;
}) => {
  if (productSettingsOrgId) {
    const response = await queries.getGenericProductSettings(apolloClient, {
      orgId: productSettingsOrgId.toString(),
    });

    if (!response) return null;

    const settings: GenericProductSettings = { ...response };

    Object.keys(response).forEach(key => {
      if (key && response[key] && response[key].disabled_types) {
        const mutatedTypes = response[key]?.disabled_types?.map(type =>
          type.toLowerCase(),
        );
        settings[key] = {
          ...response[key],
          disabled_types: mutatedTypes,
        };
      }
    });

    return settings;
  }
  return null;
};

export {
  addThumbnailsToMenuItems,
  filterMenuItems,
  filterTextures,
  filterColors,
  fetchModelData,
  fetchTextures,
  fetchPartner,
  fetchMenuItems,
  setMenuItems,
  fetchProductSettingOrgId,
  updateProductSettingsOrgId,
  fetchProductSettings,
};
